import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import WellnessScore from './WellnessScore';
import { MapPin, Phone, Calendar, User, FileText, Wallet, MessageCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getProfilePhotoUrl } from '../../lib/profileUtils';

const DEPENDANT_IMAGES = [
  // TODO: replace with commissioned photography per brand §6.1
  'https://images.unsplash.com/photo-1678225894316-b6e83cd06242?w=150&h=150&fit=crop',
  // TODO: replace with commissioned photography per brand §6.1
  'https://images.unsplash.com/photo-1559154352-06e29e1e11aa?w=150&h=150&fit=crop',
  // TODO: replace with commissioned photography per brand §6.1
  'https://images.unsplash.com/photo-1581579438747-104c53c7c1c5?w=150&h=150&fit=crop',
  // TODO: replace with commissioned photography per brand §6.1
  'https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?w=150&h=150&fit=crop',
];

const DependantCard = ({ dependant, showActions = true, onNameClick }) => {
  const navigate = useNavigate();
  const { getCareOfficerById } = useApp();

  const careOfficer = getCareOfficerById(dependant.assignedCareOfficer);
  const careOfficerPhotoUrl = getProfilePhotoUrl(careOfficer);

  const getRiskBadgeClass = (riskFlag) => {
    switch (riskFlag) {
      case 'None': return 'risk-none';
      case 'Watch': return 'risk-watch';
      case 'Concern': return 'risk-concern';
      case 'Urgent': return 'risk-urgent';
      default: return 'risk-none';
    }
  };

  const getImageForDependant = (id) => {
    const index = parseInt(id.replace('MGN-DEP-', '')) % DEPENDANT_IMAGES.length;
    return DEPENDANT_IMAGES[index];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Card 
      className="card-hover overflow-hidden"
      data-testid={`dependant-card-${dependant.id}`}
    >
      <CardContent className="p-0">
        {/* Header with risk flag */}
        <div className="relative p-4 pb-0">
          {dependant.riskFlag !== 'None' && (
            <Badge 
              className={cn(
                "absolute top-3 right-3 text-xs font-semibold",
                getRiskBadgeClass(dependant.riskFlag),
                dependant.riskFlag === 'Urgent' && 'animate-pulse-soft'
              )}
            >
              {dependant.riskFlag}
            </Badge>
          )}
          
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <Avatar className="w-16 h-16 border-2 border-border">
              <AvatarImage src={getImageForDependant(dependant.id)} alt={dependant.firstName} />
              <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">
                {dependant.firstName[0]}{dependant.lastName[0]}
              </AvatarFallback>
            </Avatar>

            {/* Info */}
            <div className="flex-1 min-w-0">
              {onNameClick ? (
                <button
                  type="button"
                  onClick={onNameClick}
                  className="block max-w-full truncate text-left font-heading text-lg font-semibold text-foreground hover:text-[#9F1D35]"
                >
                  {dependant.firstName} {dependant.lastName}
                </button>
              ) : (
                <h3 className="font-heading font-semibold text-lg text-foreground truncate">
                  {dependant.firstName} {dependant.lastName}
                </h3>
              )}
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <User className="w-3.5 h-3.5" />
                {dependant.relationship} · {dependant.age} years
              </p>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                <MapPin className="w-3.5 h-3.5" />
                {dependant.address.city}, {dependant.address.state}
              </p>
            </div>

            {/* Wellness Score */}
            <WellnessScore score={dependant.overallWellnessScore} size="md" />
          </div>
        </div>

        {/* Conditions */}
        {dependant.knownConditions.length > 0 && dependant.knownConditions[0] !== 'None reported' && (
          <div className="px-4 pt-3">
            <div className="flex flex-wrap gap-1.5">
              {dependant.knownConditions.slice(0, 3).map((condition, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="text-xs bg-muted text-muted-foreground"
                >
                  {condition}
                </Badge>
              ))}
              {dependant.knownConditions.length > 3 && (
                <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                  +{dependant.knownConditions.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Visit Info */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Last: {formatDate(dependant.lastVisitDate)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-primary font-medium">
              <Calendar className="w-4 h-4" />
              <span>Next: {formatDate(dependant.nextScheduledVisit)}</span>
            </div>
          </div>
        </div>

        {/* Care Officer */}
        {careOfficer && (
          <div className="px-4 py-2 bg-muted/50 border-t border-border">
            <div className="flex items-center gap-2">
              <Avatar className="w-6 h-6">
                <AvatarImage src={careOfficerPhotoUrl} />
                <AvatarFallback className="text-xs bg-success text-white">
                  {careOfficer.firstName[0]}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-muted-foreground">Care Officer:</span>
              <span className="text-xs font-medium text-foreground">
                {careOfficer.firstName} {careOfficer.lastName}
              </span>
              <span className="text-xs text-muted-foreground ml-auto">
                ★ {careOfficer.rating}
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="p-3 border-t border-border flex gap-2">
            <Button 
              size="sm" 
              className="flex-1"
              onClick={() => navigate(`/visits?dependant=${dependant.id}`)}
              data-testid={`view-report-${dependant.id}`}
            >
              <FileText className="w-4 h-4 mr-1.5" />
              View Report
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate('/wallet')}
              data-testid={`fund-wallet-${dependant.id}`}
            >
              <Wallet className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              data-testid={`contact-co-${dependant.id}`}
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DependantCard;
